import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useNavigate } from "react-router-dom";
import TitlePage from "../../Shared/TitlePage";
import { dataService } from "../../../Services/data/data.service";
import { Box, Dialog, DialogActions, DialogTitle, DialogContent } from "@mui/material";
import { Trash2, X } from "lucide-react";
import { Spin } from "react-cssfx-loading";
import { toast } from "react-toastify";
import { GraphFilterTypes, resultColumns, valueTypeCodes, externalApiTypes, controlTypes } from "../../../Config/constants";
import MeasurementsTable from "./Components/MeasurementsTable";

function DataManagementPage(props) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [graphList, setGraphList] = useState([]);
  const [originalGraphList, setOriginalGraphList] = useState([]); // List of all graphs
  const initialProjectList = useRef([]);
  const firstLoad = useRef(false);
  const [loadAmount, setLoadAmount] = useState(21);

  // Measurements
  const [allMeasurements, setAllMeasurements] = useState([]);
  const [measurementRows, setMeasurementRows] = useState([]);

  // Measurements table
  const [selection, setSelection] = useState([]);
  const [measurementIdSelection, setMeasurementIdSelection] = useState([]);

  // Delete
  const [measurementsToDelete, setMeasurementsToDelete] = useState([]);
  const [deleteMeasurementsDialogOpen, setDeleteMeasurementsDialogOpen] = useState(false);

  const [filters, setFilters] = useState([]);
  const [filteringDateRange, setFilteringDateRange] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const currentDate = new Date();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataService
      .getAllMeasurementsByUserId(props.Login.userid)
      .then((measurements) => {
        setAllMeasurements(measurements);
      })
      .catch((error) => {
        toast.error(`A problem occured while fetching the data`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let tempMeasurementRows = [];
    if (allMeasurements.length > 0) {
      tempMeasurementRows = allMeasurements.map((measurement) => ({
        accountId: measurement.accountId,
        accountName: measurement.accountName,
        accountCategoryId: measurement.accountCategoryId,
        accountCategoryName: measurement.accountCategoryName,
        checked: false,
        projectId: measurement.projectId,
        toDate: measurement.toDate,
        fromDate: measurement.fromDate,
        color: measurement.color,
        id: measurement.id,
        typeId: measurement.typeId,
        valueTypeId: measurement.valueTypeId,
        companyId: measurement.companyId,
        qrCode: measurement.qrCode,
        companyName: measurement.companyName,
        detailId: measurement.projectDetailId,
        projectName: measurement.projectName,
        resourceName: measurement.resourceName,
        description: measurement.description,
        startTime: measurement.startTime,
        stopTime: measurement.stopValue,
        insertedOn: new Date(measurement.insertedOn),
        updatedOn: measurement.stopTime,
        measurementSessionId: measurement.measurementSessionId,
        measurementValue: getMeasurementValue(
          measurement.typeId,
          measurement.externalApiTypeId,
          measurement.textValue,
          measurement.integerValue,
          measurement.dateValue,
          measurement.dataListValue,
          measurement.imageLink,
          measurement.externalApiValues,
          measurement.isNegative,
          measurement.numericValue
        ),
        integerValue: measurement.integerValue,
        numericValue: measurement.numericValue,
        running: measurement.running,
        thumbnail: measurement.thumbnail,
        imageId: measurement.imageId,
        commentText: getCommentText(measurement.typeId, measurement.externalApiTypeId, measurement.textValue, measurement.commentText),
        commentImage: measurement.commentImage,
        commentId: measurement.commentId,
        valueType: getValueType(measurement.valueTypeId),
        keyValue: measurement.keyValue,
        signedEventPrice: measurement.eventPrice,
        externalApiTypeId: measurement.externalApiTypeId,
        isNegative: measurement.isNegative,
      }));

      setMeasurementRows(tempMeasurementRows);

      let indexes = tempMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
      setSelection(indexes);
    }
  }, [allMeasurements]);

  const getMeasurementValue = (typeId, externalApiTypeId, text, int, time, dataListValue, imageLink, externalApiValue, isNegative, numericValue) => {
    switch (typeId) {
      case 1:
        return int;
      case 2:
        if (int > 0) {
          return time;
        } else {
          return "";
        }
      case 3:
        return text;
      case 4:
        return dataListValue;
      case 5:
        return text;
      case 6:
        return imageLink;
      case 7:
        if (externalApiTypeId === externalApiTypes.STOPWATCH) {
          if (int > 0) {
            return time;
          } else {
            return "";
          }
        } else if (externalApiTypeId === externalApiTypes.NUMERIC) {
          return isNegative ? `-${numericValue}` : numericValue;
        } else if (externalApiTypeId === externalApiTypes.TEXT) {
          return text;
        }
        return text;
      default:
        return "";
      // eslint-disable-next-line
    }
  };

  const getCommentText = (typeId, externalApiTypeId, text, commentText) => {
    switch (typeId) {
      case 1:
        return commentText;
      case 2:
        return commentText;
      case 3:
        return commentText;
      case 4:
        return commentText;
      case 5:
        return commentText;
      case 6:
        return commentText;
      case 7:
        if (externalApiTypeId === externalApiTypes.STOPWATCH) {
          return text;
        } else if (externalApiTypeId === externalApiTypes.NUMERIC) {
          return text;
        } else if (externalApiTypeId === externalApiTypes.TEXT) {
          return commentText;
        }
        return commentText;
      default:
        return "";
      // eslint-disable-next-line
    }
  };

  const getValueType = (id) => {
    switch (id) {
      case valueTypeCodes.VA:
        return "Value added";
      case valueTypeCodes.NVA:
        return "No value added";
      case valueTypeCodes.W:
        return "Waste";
      default:
        return "Unknown";
    }
  };

  const getEventPrice = (measurement) => {
    if (measurement.price === null || measurement.price === undefined || measurement.price == 0) {
      return 0;
    } else {
      if (measurement.typeId === controlTypes.STOPWATCH || (measurement.typeId === controlTypes.EXTERNALAPI && measurement.externalApiTypeId === externalApiTypes.STOPWATCH)) {
        if (measurement.startValue && measurement.stopValue) {
          const startTime = new Date(measurement.startValue);
          const stopTime = new Date(measurement.stopValue);
          const durationInSeconds = (stopTime - startTime) / 1000; // Convert to seconds
          return parseFloat((measurement.price / 3600) * durationInSeconds.toFixed(2));
        } else {
          return 0;
        }
      } else if (measurement.typeId === controlTypes.EXTERNALAPI && measurement.externalApiTypeId === externalApiTypes.NUMERIC) {
        if (measurement.numericValue !== null || measurement.numericValue !== undefined) {
          return parseFloat(measurement.numericValue * measurement.price);
        } else {
          return 0;
        }
      } else {
        return measurement.price;
      }
    }
  };

  const onSelectionChange = (e) => {
    var tempRows = JSON.parse(JSON.stringify(measurementRows));
    const selectedIds = e.map((index) => tempRows[index].id);
    setSelection(e);
  };

  const setDateRange = (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate && endDate) {
      setFilteringDateRange(true);
    } else {
      setFilteringDateRange(false);
    }
  };

  useEffect(() => {
    filterGraphs(filters);
  }, [filteringDateRange, startDate, endDate]);

  const handleSetStartDate = (event) => {
    if (event !== startDate) {
      setStartDate(event);
      setDateRange(event, endDate);
    }
  };
  const handleSetEndDate = (event) => {
    if (event !== endDate) {
      setEndDate(event);
      setDateRange(startDate, event);
    }
  };

  const resetDateRangeFilter = () => {
    setDateRange(null, null);
  };

  const updateFilters = (type, event) => {
    let tempFilters = [...filters];
    if (tempFilters.some((x) => x.type === type)) {
      let index = tempFilters.indexOf(tempFilters.find((x) => x.type === type));
      tempFilters.splice(index, 1);
      tempFilters.unshift({ type: type, filterValue: event.target.value });
    } else {
      tempFilters.unshift({ type: type, filterValue: event.target.value });
    }

    setFilters(tempFilters);
    filterGraphs(tempFilters);
  };

  const filterGraphs = (thisFilters) => {
    let varFilteredGraphs = [...originalGraphList];
    if (filteringDateRange) {
      varFilteredGraphs = varFilteredGraphs.filter((x) => new Date(x.updatedOn || x.insertedOn) > new Date(startDate) && new Date(x.updatedOn || x.insertedOn) < new Date(endDate));
    }

    for (let i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case GraphFilterTypes.GENERAL:
          varFilteredGraphs = varFilteredGraphs.filter(
            (x) => x.companyName.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.name.toLowerCase().includes(filter.filterValue.toLowerCase())
          );
          break;
        default:
          varFilteredGraphs = varFilteredGraphs.filter((x) => x.companyName.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
      }
    }
    setGraphList(varFilteredGraphs);
  };

  const handleDeleteMeasurement = (measurementId) => {
    setMeasurementsToDelete([...measurementsToDelete, measurementId]);
    setDeleteMeasurementsDialogOpen(true);
  };

  const handleBatchDelete = () => {
    // find the id's of the measurements in selection and add them to the delete list
    let tempMeasurementsToDelete = [...measurementsToDelete];
    selection.forEach((index) => {
      tempMeasurementsToDelete.push(measurementRows[index].id);
    });

    setMeasurementsToDelete(tempMeasurementsToDelete);
    setDeleteMeasurementsDialogOpen(true);
  };

  // "Delete" multiple measurements at once
  const deleteSelectedMeasurements = () => {
    dataService
      .setMeasurementsInactiveByIdList(measurementsToDelete)
      .then((result) => {
        toast.success(`The measurements were removed`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        let tempMeasurementRows = [...measurementRows];
        let tempSelection = [...selection];
        measurementsToDelete.forEach((measurementId) => {
          let index = tempMeasurementRows.findIndex((x) => x.id === measurementId);
          if (index > -1) {
            tempMeasurementRows.splice(index, 1);
            let selectionIndex = tempSelection.findIndex((x) => x === index);
            if (selectionIndex > -1) {
              tempSelection.splice(selectionIndex, 1);
            }
          }
        });
        setMeasurementRows(tempMeasurementRows);
        setSelection([]);
        setMeasurementsToDelete([]);
        handleCloseDeleteMeasurementsDialog();
      })
      .catch((error) => {
        toast.error(`A problem occured while deleting the data`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      });
  };

  const setMeasurementFinished = (measurementId) => {
    let tempMeasurementRows = [...measurementRows];
    let index = tempMeasurementRows.findIndex((x) => x.id === measurementId);
    if (index > -1) {
      tempMeasurementRows[index].running = false;
    }
    setMeasurementRows(tempMeasurementRows);
  };

  const handleCloseDeleteMeasurementsDialog = () => {
    setDeleteMeasurementsDialogOpen(false);
    setMeasurementsToDelete([]);
  };

  const handleLocalUpdate = (measurement) => {
    let newMeasurement = {
      ...measurement,
      measurementValue: getMeasurementValue(
        measurement.typeId,
        measurement.externalApiTypeId,
        measurement.textValue,
        measurement.integerValue,
        measurement.dateValue,
        measurement.dataListValue,
        measurement.imageLink,
        measurement.externalApiValues,
        measurement.isNegative,
        measurement.numericValue
      ),
      commentText: getCommentText(measurement.typeId, measurement.externalApiTypeId, measurement.textValue, measurement.commentText),
      valueType: getValueType(measurement.valueTypeId),
      signedEventPrice: measurement.eventPrice,
    };

    // update the measurement in the local state
    let tempMeasurementRows = [...measurementRows];
    let index = tempMeasurementRows.findIndex((x) => x.id === measurement.id);
    if (index > -1) {
      tempMeasurementRows[index] = newMeasurement;
      setMeasurementRows(tempMeasurementRows);
    }
  };

  return (
    <React.Fragment>
      {loading && (
        <div className={classes.loadingChart}>
          <Spin width="100px" height="100px" color="#C3996B"></Spin>
        </div>
      )}
      <TitlePage Text="Graphs overview" backbutton={true} {...props} />
      <Box className={classes.container}>
        <div className={classes.header} sx={{ paddingBottom: "16px", display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%" }}>
          <h1>Datamanagement</h1>
          <button className={classes.tableManagementContainer__deleteButton} disabled={selection.length === 0} onClick={() => handleBatchDelete()}>
            <Trash2 className={classes.iconButton__icon} />
            Delete selected
          </button>
        </div>
        <div className={classes.measurementsTableContainer}>
          <MeasurementsTable
            data={measurementRows}
            columns={resultColumns}
            onSelectionChange={onSelectionChange}
            selection={selection}
            handleDeleteMeasurement={handleDeleteMeasurement}
            handleLocalUpdate={handleLocalUpdate}
            setMeasurementFinished={setMeasurementFinished}
            {...props}
          />
        </div>
      </Box>
      {deleteMeasurementsDialogOpen && (
        <Dialog
          className={classes.dialog}
          open={deleteMeasurementsDialogOpen}
          onClose={() => handleCloseDeleteMeasurementsDialog()}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          classes={{ paper: classes.dialogPaper }}
        >
          <div className={classes.dialog__head}>
            <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
              Delete measurement{measurementsToDelete.length > 1 ? "s" : ""}
            </DialogTitle>
            <button onClick={() => handleCloseDeleteMeasurementsDialog()} className={classes.dialog__closeButton}>
              <X size={19} />
            </button>
          </div>
          <DialogContent className={classes.dialog__contentContainer}>
            <div className={classes.dialog__content}>
              <p className={classes.dialog__subtitle}>
                Are you sure you want to delete {measurementsToDelete.length} measurement{measurementsToDelete.length > 1 ? "s" : ""}?
              </p>
            </div>
          </DialogContent>
          <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
            <button className={classes.secondaryButton} onClick={() => handleCloseDeleteMeasurementsDialog()}>
              Cancel
            </button>
            <button className={classes.deleteButton} onClick={() => deleteSelectedMeasurements()}>
              <Trash2 className={classes.iconButton__icon} />
              {"Delete"}
            </button>
          </DialogActions>
        </Dialog>
      )}
    </React.Fragment>
  );
}

export default DataManagementPage;

const useStyles = makeStyles((theme) => ({
  container: {
    boxSizing: "border-box",

    borderRadius: "8px",
    maxWidth: "90%",
    marginRight: "auto",
    marginLeft: "auto",
    marginTop: "120px",
    marginBottom: "24px",
  },
  deleteButton: {
    padding: "0 16px",
    paddingLeft: "8px",
    border: "1px solid #D84949  !important",
    borderRadius: "4px",
    fontSize: "16px",
    color: "#D84949 !important",
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      border: "1px solid #D84949",
      backgroundColor: "#D84949",
      color: "white  !important",
      cursor: "pointer",
    },
  },
  header: {
    width: "100%",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
  },

  loadingChart: {
    height: "100vh",
    width: "100%",
    top: "0px",
    position: "fixed",
    zIndex: "999",
    borderRadius: "8px",
    backgroundColor: "rgba(156,156,156,0.5)",
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
  },

  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",

    flexWrap: "wrap",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid #C3996B",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  tableManagementContainer__deleteButton: {
    height: "32px",
    padding: "0 12px",
    paddingLeft: "6px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#D84949",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    userSelect: "none",
    opacity: "1",

    transition: "all 0.2s ease-in-out",

    // [theme.breakpoints.down('sm')]: {
    //     height: "40px",
    //     paddingLeft: "8px",
    // },

    "&:hover": {
      backgroundColor: "#C73B3B",
      color: "white",
      cursor: "pointer",
    },

    "&:disabled": {
      opacity: "0",
      visibility: "hidden",
      cursor: "default",
    },
  },

  primaryIconButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#C3996B",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  measurementsTableContainer: {
    height: "900px",
    backgroundColor: "white",
    padding: "16px",
    borderRadius: "8px",
  },

  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "320px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    lineHeight: "100% !important",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 8px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "8px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  deleteButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#FDF3F3",
    backgroundColor: "#D84949",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#C73B3B",
      color: "FDF3F3",
      cursor: "pointer",
    },
  },

  secondaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "1px solid #C3996B",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#C3996B",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      border: "1px solid #a77d4f",
      cursor: "pointer",
    },
  },
}));
